import { Fragment, useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle, CloudArrowUp, X } from '@phosphor-icons/react';
import { acknowledgeSent, startOutboxSync } from '../lib/outbox.ts';
import { counterDetail, useOutbox } from './outbox-panel.tsx';

/** Ein leerer Zähler allein sähe aus wie ein Verwerfen: Hier steht, was ohne die Person rausgegangen ist. */
export function OutboxSentBand({ links = false }: { links?: boolean }) {
  const fresh = useOutbox().sent.filter((s) => !s.seen);
  if (!fresh.length) return null;
  return (
    <div className="flex items-center justify-center gap-2 bg-ok px-4 py-1 text-center text-[15px] font-semibold text-white" role="status">
      <CheckCircle size={20} weight="bold" className="shrink-0" aria-hidden />
      <span>
        Gesendet:{' '}
        {fresh.map((s, i) => (
          <Fragment key={s.id}>
            {i ? ', ' : ''}
            {links ? (
              <Link to="/tickets/$number" params={{ number: s.number }} className="text-white underline underline-offset-2">
                {s.number}
              </Link>
            ) : (
              s.number
            )}
          </Fragment>
        ))}
      </span>
      <button type="button" aria-label="Hinweis schließen" className="inline-flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center" onClick={() => fresh.forEach((s) => acknowledgeSent(s.id))}>
        <X size={18} weight="bold" aria-hidden />
      </button>
    </div>
  );
}

/**
 * Sendet den Ausgang aus jeder Seite nach. Hängt an der Wurzelroute, weil Kiosk und Schnellmeldung
 * keine `AppShell` haben. Dort zeigt er auch den Zähler: Das Gerät gehört allen, darum nur Anzahl
 * und Uhrzeit, nie Name oder Text. Klebt am unteren Rand und deckt so das Seitenende nicht zu.
 */
export function OutboxAgent() {
  const qc = useQueryClient();
  const { reports, sent, hosted } = useOutbox();
  const delivered = sent.length;

  useEffect(startOutboxSync, []);
  useEffect(() => {
    if (delivered) void qc.invalidateQueries({ queryKey: ['tickets'] });
  }, [qc, delivered]);

  if (hosted || (!reports.length && !sent.some((s) => !s.seen))) return null;

  const { text, rejected } = counterDetail(reports);
  return (
    <div className="no-print sticky bottom-0 z-[250] bg-steel-900 pb-[env(safe-area-inset-bottom)]">
      {reports.length ? (
        <div className={`flex items-center justify-center gap-2 px-4 py-2 text-center text-[15px] font-semibold text-white ${rejected ? 'bg-error' : 'bg-warn'}`} role="status">
          <CloudArrowUp size={20} weight="bold" className="shrink-0" aria-hidden />
          <span>{text}</span>
        </div>
      ) : null}
      <OutboxSentBand />
    </div>
  );
}
